import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Rocket, ShieldAlert, Loader2, CheckCircle2, XCircle, AlertTriangle, Camera, LifeBuoy, Play } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const VERDICT = {
  go: { label: 'GO', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40', icon: CheckCircle2 },
  limited_go: { label: 'LIMITED GO', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40', icon: AlertTriangle },
  no_go: { label: 'NO GO', cls: 'bg-red-500/15 text-red-400 border-red-500/40', icon: XCircle },
};

// Compact human string of a criterion's observed evidence (falls back to threshold).
const fmtObserved = (o) => {
  if (!o || typeof o !== 'object') return '';
  const parts = [];
  if ('critical' in o) parts.push(o.critical == null ? 'not measured' : `${o.critical} critical`);
  if (o.blocked_pct != null) parts.push(`${o.blocked_pct}% blocked`);
  if (o.data_leak != null) parts.push(o.data_leak ? 'data leak!' : 'no leak');
  if (o.p95_ms != null) parts.push(`p95 ${Math.round(o.p95_ms)}ms`);
  if (o.error_rate_pct != null) parts.push(`${o.error_rate_pct}% err`);
  if (o.unhandled_errors != null) parts.push(`${o.unhandled_errors} unhandled`);
  if (o.model_unavailable_graceful != null) parts.push(o.model_unavailable_graceful ? 'model-off ok' : 'model-off fails');
  if (o.integrity_pct != null) parts.push(`${o.integrity_pct}% integrity`);
  return parts.join(' · ');
};

export default function AdminPilotConsolePage() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [readinessNotes, setReadinessNotes] = useState([]);
  const [readinessMeta, setReadinessMeta] = useState(null);
  const [running, setRunning] = useState(false);
  const [roleInput, setRoleInput] = useState('');

  const load = async () => {
    try {
      const [c, m, e, r] = await Promise.all([
        api.getPilotConfig(), api.getPilotMetrics(), api.getPilotEscalations(), api.getPilotReadiness(),
      ]);
      setCfg(c); setMetrics(m); setEscalations(e.escalations || []);
      setReadiness(r.result || null); setReadinessNotes(r.notes || []);
      setReadinessMeta(r.result ? { method: r.method, at: r.at, by: r.by } : null);
    } catch (err) { toast.error(err.message || 'Could not load the pilot console'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runReadiness = async () => {
    setRunning(true);
    try {
      const r = await api.runPilotReadiness();
      setReadiness(r.result || null); setReadinessNotes(r.notes || []);
      setReadinessMeta({ method: r.method, at: new Date().toISOString(), by: 'you' });
      const verdict = r.result?.verdict;
      toast[verdict === 'no_go' ? 'error' : verdict === 'go' ? 'success' : 'message'](
        `Readiness check complete — ${(VERDICT[verdict] || {}).label || 'done'}`);
    } catch (e) { toast.error(e.message || 'Readiness check failed'); }
    finally { setRunning(false); }
  };

  const save = async (patch) => {
    try { const c = await api.setPilotConfig(patch); setCfg((p) => ({ ...c, model_enabled: p.model_enabled, capabilities_all: p.capabilities_all })); toast.success('Pilot flags saved'); }
    catch (e) { toast.error(e.message); }
  };
  const toggleCap = (cap, val) => save({ capabilities: { ...(cfg.capabilities || {}), [cap]: val } });
  const addRole = () => { const r = roleInput.trim(); if (!r) return; save({ roles: [...(cfg.roles || []), r] }); setRoleInput(''); };
  const removeRole = (r) => save({ roles: (cfg.roles || []).filter((x) => x !== r) });

  const toggleModel = async (val) => {
    try { await api.setPilotModelSwitch(val); setCfg((p) => ({ ...p, model_enabled: val })); toast.success(`Model ${val ? 'enabled' : 'killed'}`); }
    catch (e) { toast.error(e.message); }
  };
  const toggleIncident = async () => {
    const next = !cfg.incident_paused;
    try { const c = await api.setPilotIncident(next, next ? 'Paused from console' : ''); setCfg((p) => ({ ...c, model_enabled: p.model_enabled, capabilities_all: p.capabilities_all })); toast[next ? 'error' : 'success'](next ? 'PILOT PAUSED' : 'Pilot resumed'); }
    catch (e) { toast.error(e.message); }
  };
  const snapshot = async () => { try { await api.snapshotPilotBaseline(); await load(); toast.success('Baseline captured'); } catch (e) { toast.error(e.message); } };
  const resolveEsc = async (id) => { try { await api.resolvePilotEscalation(id); setEscalations((e) => e.map((x) => x.escalation_id === id ? { ...x, status: 'resolved' } : x)); } catch (e) { toast.error(e.message); } };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  const v = readiness ? (VERDICT[readiness.verdict] || VERDICT.no_go) : null;
  const m = metrics?.metrics || {};
  const cmp = metrics?.comparison || {};

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="admin-pilot-console">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center"><Rocket className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Coach Pilot Console</h1>
          <p className="text-sm text-muted-foreground">Controlled-pilot flags, kill switches, redacted metrics, escalations, and readiness.</p>
        </div>
      </div>

      {/* Readiness verdict */}
      <Card className={`p-5 border ${v ? v.cls : 'border-border/50'}`} data-testid="readiness-banner">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {v ? (
              <div>
                <div className="flex items-center gap-2 text-lg font-bold"><v.icon className="w-5 h-5" /> Recommendation: <span data-testid="readiness-verdict">{v.label}</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-3">
                  {Object.entries(readiness.criteria || {}).map(([k, c]) => (
                    <div key={k} className="text-[11px]" data-testid={`criterion-${k}`}>
                      {c.advisory ? (
                        <span className="text-muted-foreground">◦ {k} <span className="text-amber-400/90">(advisory)</span></span>
                      ) : (
                        <span className={c.pass ? 'text-emerald-400' : 'text-red-400'}>{c.pass ? '✓' : '✗'} {k}</span>
                      )}
                      <p className="text-muted-foreground">{c.advisory ? 'not verified in-app' : (fmtObserved(c.observed) || c.threshold)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground">No readiness evaluation yet — press <strong>Run readiness check</strong> to compute a verdict for this environment.</p>}
          </div>
          <Button size="sm" onClick={runReadiness} disabled={running} data-testid="run-readiness" className="gap-1.5 shrink-0">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} {running ? 'Running…' : 'Run readiness check'}
          </Button>
        </div>
        {readinessMeta && readinessMeta.method === 'manual' && (
          <div className="mt-3 flex items-start gap-1.5 text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-2 py-1.5" data-testid="readiness-manual-warning" role="alert">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>This verdict was computed from <strong>operator-supplied evidence</strong> — not independently verified. Run the in-app check or the offline validation for trustworthy evidence.</span>
          </div>
        )}
        {readinessMeta && readinessMeta.method && readinessMeta.method !== 'manual' && (
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="readiness-provenance">
            Source: {readinessMeta.method === 'selfcheck' ? 'in-app self-check (server-computed)' : 'offline validation run'}{readinessMeta.at ? ` · ${new Date(readinessMeta.at).toLocaleString()}` : ''}
          </p>
        )}
        {readinessNotes.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="readiness-notes">
            {readinessNotes.map((n, i) => (
              <li key={i} className="text-[11px] text-muted-foreground flex gap-1.5"><span className="text-primary">•</span>{n}</li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-muted-foreground mt-2">Evidence-based (thresholds, not momentum). The in-app check runs governance, security &amp; resilience live; run <code>gate5_pilot_validation</code> offline for a full axe + external load run.</p>
      </Card>

      {/* Kill switches / incident */}
      <Card className="p-5 space-y-3">
        <p className="text-sm font-semibold text-foreground">Kill switches & incident control</p>
        <div className="flex items-center justify-between">
          <div><p className="text-sm">Global model (generative AI)</p><p className="text-xs text-muted-foreground">Off → deterministic fallback everywhere.</p></div>
          <Switch checked={!!cfg.model_enabled} onCheckedChange={toggleModel} aria-label="Global model kill switch" data-testid="model-kill-switch" />
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-border/40">
          <div><p className="text-sm text-red-400 font-medium flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Incident pause</p><p className="text-xs text-muted-foreground">Instantly stops the Coach for all pilot users.</p></div>
          <Button variant={cfg.incident_paused ? 'default' : 'destructive'} size="sm" onClick={toggleIncident} data-testid="incident-toggle">
            {cfg.incident_paused ? 'Resume pilot' : 'Pause pilot (incident)'}
          </Button>
        </div>
      </Card>

      {/* Capability + role flags */}
      <Card className="p-5 space-y-4">
        <p className="text-sm font-semibold text-foreground">Capability flags</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(cfg.capabilities_all || []).map((cap) => (
            <div key={cap} className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2">
              <span className="text-xs capitalize">{cap}</span>
              <Switch checked={!!(cfg.capabilities || {})[cap]} onCheckedChange={(v2) => toggleCap(cap, v2)} aria-label={`Toggle ${cap} capability`} data-testid={`cap-${cap}`} />
            </div>
          ))}
        </div>
        <div className="pt-2 border-t border-border/40">
          <p className="text-xs text-muted-foreground mb-1.5">Role allow-list <span className="opacity-70">(empty = all roles)</span></p>
          <div className="flex items-center gap-2 flex-wrap">
            {(cfg.roles || []).map((r) => (
              <Badge key={r} className="bg-primary/10 text-primary border-primary/30 cursor-pointer" onClick={() => removeRole(r)} data-testid={`role-${r}`}>{r} ✕</Badge>
            ))}
            <Input value={roleInput} onChange={(e) => setRoleInput(e.target.value)} placeholder="add role…" className="h-7 w-32 text-xs" data-testid="role-input" />
            <Button size="sm" variant="outline" onClick={addRole} data-testid="role-add">Add</Button>
          </div>
        </div>
      </Card>

      {/* Redacted metrics */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Operational metrics <span className="text-xs text-muted-foreground font-normal">(redacted — counts only)</span></p>
          <Button size="sm" variant="outline" onClick={snapshot} data-testid="snapshot-baseline" className="gap-1.5"><Camera className="w-3.5 h-3.5" /> Snapshot baseline</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pilot-metrics">
          {[['Sessions', 'sessions_started'], ['Completed', 'sessions_completed'], ['Completion %', 'completion_rate_pct'],
            ['Deviations', 'deviations'], ['Recoveries', 'recoveries_offered'], ['Intent reqs', 'intent_requests'],
            ['Repairs', 'repair_proposals'], ['Escalations open', 'escalations_open']].map(([label, key]) => (
            <div key={key} className="rounded-lg border border-border/50 p-3">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className="text-xl font-semibold text-foreground">{m[key] ?? 0}</p>
              {cmp[key] && cmp[key].delta != null && <p className="text-[10px] text-muted-foreground">Δ {cmp[key].delta > 0 ? '+' : ''}{cmp[key].delta} vs baseline</p>}
            </div>
          ))}
        </div>
      </Card>

      {/* Escalations */}
      <Card className="p-5 space-y-3">
        <p className="text-sm font-semibold text-foreground flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-primary" /> Support escalations</p>
        {escalations.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="no-escalations">No escalations.</p> : (
          <div className="space-y-2" data-testid="escalations-list">
            {escalations.map((e) => (
              <div key={e.escalation_id} className="flex items-start justify-between gap-3 rounded-lg border border-border/50 p-3">
                <div>
                  <div className="text-xs font-medium flex items-center">{e.category} <Badge className={`ml-1 text-[10px] ${e.status === 'open' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{e.status}</Badge></div>
                  <p className="text-[12px] text-muted-foreground mt-0.5">{e.note}</p>
                </div>
                {e.status === 'open' && <Button size="sm" variant="ghost" onClick={() => resolveEsc(e.escalation_id)} data-testid={`resolve-${e.escalation_id}`}>Resolve</Button>}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
